/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/



import OfficeVisitTable from "components/tables/OfficeVisitTable";
import useHttp from "hooks/http.hook";
import { FC, useEffect, useState } from "react";
import { AppointmentInterface, QueryResultInterface } from "medicalsystem-shared";
import { CoreAPI_URLs } from "constants/http.const";
import Box from "components/generals/Box";
import { useTranslation } from "react-i18next";
import { getRangeDateQuery } from "utils/http.utils";
import Autocomplete from "components/generals/Autocomplete";
import TextField from "@mui/material/TextField";
import moment from "moment";

/**
 * Content component for displaying and managing office visit appointments.
 * Shows a table of all office visits with filtering, date range selection, and pagination.
 * Provides functionality for viewing and managing scheduled patient visits.
 * @returns {React.ReactElement} The office visit data content component
 */
const OfficeVisitDataContent: FC = () => {
  const [isLoading, _error, sendRequest] = useHttp();
  const [officevisitListResponse, setOfficevisitListResponse] = useState<AppointmentInterface[]>(
    []
  );
  const [page, setPage] = useState<number>(1);
  const [pageTotal, setPageTotal] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [rowsTotal, setRowsTotal] = useState<number>(0);
  const [filterModel, setFilterModel] = useState<string>("");
  const [dateFilter, setDateFilter] = useState<string>("day"); // 'day', 'week', 'month'

  const { t } = useTranslation();
  const dateFilterOptions = [
    { id: "day", label: (t("officevisitDataContent.filterDay")) },
    { id: "week", label: (t("officevisitDataContent.filterWeek")) },
    { id: "month", label: (t("officevisitDataContent.filterMonth")) },
  ];

  useEffect(() => {
    let startDate, endDate;
    if (dateFilter === "day") {
      startDate = moment().startOf("day").toDate();
      endDate = moment().endOf("day").toDate();
    } else if (dateFilter === "week") {
      startDate = moment().startOf("week").toDate();
      endDate = moment().endOf("week").toDate();
    } else if (dateFilter === "month") {
      startDate = moment().startOf("month").toDate();
      endDate = moment().endOf("month").toDate();
    }
    const range = getRangeDateQuery(startDate, endDate);

    const nameParam = filterModel ? `patientName=${filterModel}` : "";
    const url = `${CoreAPI_URLs.APPOINTMENTS_GET_ALL}${range}&${nameParam}&page=${page}&pageSize=${pageSize}`;

    sendRequest(url, onGetOfficeVisitHandler, "GET");
  }, [page, pageSize, filterModel, dateFilter]);

  const onGetOfficeVisitHandler = (
    officevisitResponse: QueryResultInterface<AppointmentInterface>
  ) => {
    setOfficevisitListResponse(officevisitResponse.results);
    setRowsTotal(officevisitResponse.total);
    setPageTotal(Math.ceil(officevisitResponse.total / pageSize));
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'inline-block', minWidth: 300 }}>
          <Autocomplete
            options={dateFilterOptions}
            autoCompleteValue={dateFilterOptions.find((o) => o.id === dateFilter) || dateFilterOptions[0]}
            onChange={(_event, newValue) => {
              if (newValue) setDateFilter(newValue.id);
            }}
            getOptionLabel={(option) => option.label}
            id="date-filter-autocomplete"
            label={t("officevisitDataContent.dateFilterLabel")}
            formikOutside={true}
          />
        </Box>
      </Box>
      <OfficeVisitTable
        modelList={officevisitListResponse}
        setModelList={setOfficevisitListResponse}
        canSearchPlaceholder={t("officevisitDataContent.search")}
        entriesPerPage={{ defaultValue: pageSize }}
        setPageState={setPage}
        setPageSizeState={setPageSize}
        apiPagination={true}
        apiPageTotal={pageTotal}
        apiRowsTotal={rowsTotal}
        apiPageCurrentIndex={page}
        setModelFilter={setFilterModel}
        isLoading={isLoading}
      />
    </Box>
  );
};

export default OfficeVisitDataContent;
